import StudentAnswer from 'components/profesores/StudentAnswer';
import React from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Input,
  Textarea,
  Button,
  Heading,
  useToast,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FaPencilAlt, FaCheck, FaTimes, FaDownload } from 'react-icons/fa';

import xhr from 'xhr';
import AnnotatableDocument from 'components/AnnotatableDocument';
import BasicModal from 'components/modal/Modal';

const PRESETS = [
  'Has dado una excelente respuesta, demostrando comprensión del tema, claridad en tus ideas y buen manejo de la información. Continúa trabajando con ese mismo compromiso y dedicación.',
  'Has realizado un buen trabajo y evidencias un proceso adecuado en el desarrollo de la actividad. Te felicito por tu esfuerzo; continúa así, manteniendo esa disposición y compromiso con tu aprendizaje.',
  'Tu trabajo va por buen camino; sin embargo, es importante que profundices un poco más en el tema para ampliar tus ideas y fortalecer la información presentada. Te recomiendo explicar con mayor detalle tus respuestas y apoyar mejor tus planteamientos.',
  'Tu trabajo no cumple con los parámetros solicitados para esta actividad.',
];

function filesFromSitting(sitting) {
  if (!sitting) return [];
  if (sitting.file) {
    return [{
      url: sitting.file.url,
      mime: sitting.file.mime || sitting.file.ext,
      name: sitting.file.name,
    }];
  }
  if (Array.isArray(sitting.files) && sitting.files.length) {
    return sitting.files.map((f) => ({
      url: f.url,
      mime: f.mime || f.ext,
      name: f.name,
    }));
  }
  return [];
}

function programLabel(subject) {
  if (!subject) return '';
  const grade = subject.grade?.name;
  const career = subject.technical_career?.name;
  const course = subject.course?.name;
  return grade || career || course || '';
}

function programTypeLabel(subject) {
  if (!subject) return '';
  return (
    subject.grade?.program_type?.name ||
    subject.technical_career?.program_type?.name ||
    subject.course?.program_type?.name ||
    ''
  );
}

const ActivityDetailAnnotated = (props) => {
  const { score, setScore, setIsOpen } = props;
  const [currentSitting, setCurrentSitting] = React.useState({});
  const [noteText, setNoteText] = React.useState('');
  const [annotations, setAnnotations] = React.useState(null);
  const [isFetching, setIsFetching] = React.useState(false);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const toast = useToast();

  React.useEffect(() => {
    setCurrentSitting(props.sitting);
    setAnnotations(props.sitting?.teacher_annotations || { version: 1, items: [] });
  }, [props.sitting.id]);

  const files = filesFromSitting(currentSitting);
  const annotationsCount = annotations?.items?.length || 0;
  const checkCount = (annotations?.items || []).filter((a) => a.type === 'check').length;
  const xCount = (annotations?.items || []).filter((a) => a.type === 'x').length;

  const handleSave = async () => {
    try {
      if (!score || !noteText) {
        return alert('Debes diligenciar la nota y el mensaje');
      }
      if (score < 0 || score > 10) {
        return alert('La nota debe ser entre 0 y 10');
      }

      setIsFetching(true);

      await xhr.put(`/activity-sittings/${currentSitting.id}`, {
        data: {
          score,
          answer: noteText,
          user: currentSitting.student.user,
          activity: currentSitting.activity,
          teacher_annotations: annotations,
        },
      });

      setScore('');
      setNoteText('');
      toast({
        title: '¡La calificación se envió correctamente!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      props.onSave();
      setIsOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleDeleteActivity = async () => {
    try {
      setIsFetching(true);
      await xhr.delete(`/activity-sittings/${currentSitting.id}`);
      toast({
        title: 'La actividad se eliminó correctamente!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      props.onSave();
      setScore('');
      setNoteText('');
      setIsOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  if (!currentSitting.id) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" pb={3} mb={4} borderBottom="1px solid" borderColor="gray.200">
        <Box>
          <Text fontSize="11px" color="#574feb" fontWeight="500" textTransform="uppercase" letterSpacing="0.05em" mb={1}>
            {programTypeLabel(currentSitting.subject)} {programLabel(currentSitting.subject) && `· ${programLabel(currentSitting.subject)}`} {currentSitting.subject?.name && `· ${currentSitting.subject.name}`}
          </Text>
          <Text fontSize="18px" fontWeight="500" mb={1}>
            {currentSitting.activity?.title || 'Actividad'}
          </Text>
          <Text fontSize="13px" color="gray.500">
            Respuesta de{' '}
            <Text as="span" color="gray.800" fontWeight="500">
              {`${currentSitting.student?.user?.first_name || ''} ${currentSitting.student?.user?.last_name || ''}`}
            </Text>{' '}
            (Cod. {currentSitting.student?.id}) · enviado{' '}
            {dayjs(currentSitting.createdAt).fromNow()}
          </Text>
        </Box>
        <Link to={`/plataforma/profesores/chat?user=${currentSitting.student?.user?.id}`}>
          <Button colorScheme="messenger" size="xs">
            Enviar mensaje
          </Button>
        </Link>
      </Box>

      <Box mb={4}>
        <Text fontSize="13px" fontWeight="500" mb={2}>
          Enunciado de la actividad
        </Text>
        <Box
          fontSize="13px"
          color="gray.700"
          lineHeight="1.55"
          dangerouslySetInnerHTML={{ __html: currentSitting.activity?.content || '' }}
        />
      </Box>

      <Flex justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Text fontSize="13px" fontWeight="500">
          Entrega de {currentSitting.student?.user?.first_name}
        </Text>
        <Flex gap={2}>
          {files.length > 0 && (
            <Button
              as="a"
              href={files[0].url}
              target="_blank"
              rel="noreferrer"
              size="sm"
              variant="outline"
              leftIcon={<FaDownload size={11} />}
              color="gray.600"
            >
              Original
            </Button>
          )}
          {files.length > 0 && (
            <Button
              size="sm"
              bg="#574feb"
              color="white"
              _hover={{ bg: '#3c3489' }}
              leftIcon={<FaPencilAlt size={11} />}
              onClick={() => setIsEditorOpen(true)}
            >
              {annotationsCount > 0 ? 'Editar anotaciones' : 'Anotar entrega'}
            </Button>
          )}
        </Flex>
      </Flex>

      <Box mb={4}>
        {files.length > 0 ? (
          <AnnotatableDocument
            files={files}
            value={annotations}
            onChange={setAnnotations}
            readonly={true}
          />
        ) : (
          <StudentAnswer
            html={currentSitting.content}
            emptyHtml="<em>Sin contenido</em>"
            p={4}
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            fontSize="13px"
            color="gray.600"
          />
        )}
      </Box>

      <BasicModal isOpen={isEditorOpen} setIsOpen={setIsEditorOpen} size="6xl">
        <Box mt={2}>
          <Heading size="md" mb={1}>
            Anotar entrega
          </Heading>
          <Text fontSize="13px" color="gray.500" mb={4}>
            Marca, escribe o pon checks sobre el documento. Al cerrar este modal, los cambios quedan visibles en la vista previa. La calificación se envía con el botón "Enviar calificación".
          </Text>
          <AnnotatableDocument
            files={files}
            value={annotations}
            onChange={setAnnotations}
            readonly={false}
            maxWidth={900}
          />
          <Flex justifyContent="flex-end" mt={4}>
            <Button
              bg="#574feb"
              color="white"
              _hover={{ bg: '#3c3489' }}
              onClick={() => setIsEditorOpen(false)}
            >
              Listo
            </Button>
          </Flex>
        </Box>
      </BasicModal>

      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3} mb={4}>
        <GridItem>
          <Text fontSize="13px" fontWeight="500" mb={1}>
            Nota (0 a 10)
          </Text>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="10"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            fontSize="18px"
            fontWeight="500"
            color="#574feb"
          />
        </GridItem>
        <GridItem>
          <Text fontSize="13px" fontWeight="500" mb={1}>
            Resumen visual
          </Text>
          <Flex
            px={3}
            py={2}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            bg="gray.50"
            fontSize="12px"
            color="gray.600"
            alignItems="center"
            gap={2}
            h="40px"
          >
            <FaPencilAlt color="#574feb" size={12} />
            <Text>{annotationsCount} anotaciones</Text>
            <Box w="1px" h="14px" bg="gray.300" />
            <FaCheck color="#1d9e75" size={12} />
            <Text>{checkCount}</Text>
            <Box w="1px" h="14px" bg="gray.300" />
            <FaTimes color="#e24b4a" size={12} />
            <Text>{xCount}</Text>
          </Flex>
        </GridItem>
      </Grid>

      <Box mb={4}>
        <Text fontSize="13px" fontWeight="500" mb={2}>
          Mensaje al estudiante
        </Text>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={2} mb={2}>
          {PRESETS.map((preset, idx) => {
            const active = noteText === preset;
            return (
              <Box
                key={idx}
                as="button"
                type="button"
                onClick={() => setNoteText(preset)}
                p={3}
                fontSize="12px"
                textAlign="left"
                border="1px solid"
                borderColor={active ? '#574feb' : 'gray.200'}
                bg={active ? '#f3f1ff' : 'white'}
                color={active ? '#3c3489' : 'gray.700'}
                borderRadius="md"
                cursor="pointer"
                lineHeight="1.5"
                _hover={{ borderColor: '#574feb' }}
              >
                {preset.length > 80 ? `${preset.slice(0, 80)}…` : preset}
              </Box>
            );
          })}
        </Grid>
        <Textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          rows={3}
          fontSize="13px"
          placeholder="Escribe el mensaje para el estudiante o elige una opción arriba."
        />
      </Box>

      <Flex justifyContent="space-between" alignItems="center" pt={3} borderTop="1px solid" borderColor="gray.200">
        <Button
          variant="outline"
          colorScheme="red"
          size="sm"
          onClick={() => {
            if (window.confirm('¿Está seguro de eliminar esta entrega?')) {
              handleDeleteActivity();
            }
          }}
        >
          Eliminar entrega
        </Button>
        <Button
          bg="#574feb"
          color="white"
          _hover={{ bg: '#3c3489' }}
          size="md"
          onClick={handleSave}
          isLoading={isFetching}
        >
          Enviar calificación
        </Button>
      </Flex>
    </Box>
  );
};

ActivityDetailAnnotated.defaultProps = {
  sitting: {
    activity: {},
    student: { user: {} },
    material: {},
    module: {},
    program: {},
  },
};

export default ActivityDetailAnnotated;
